import { AiGeneratedArtifacts, AiGeneratedDocument } from '@klab-berlin/api-sdk/lib/types/responses/Ai';
import api from '../clients/api';
import { handleUnauthorized } from '../utils/apiWrapper';
import { notifyBugsnagNetwork } from '../utils/bugsnagClient';
import {
  GenerateLernsetWorksheet,
  AIConversationAction,
  GenerateSuggestions,
  GeneratedArtifactsQuery,
  GeneratedPodcastQuery,
  UpdateGeneratedPodcast,
} from '@klab-berlin/api-sdk/lib/types/requests/Ai';

const service = {
  generateLernsetWorksheet,
  streamConversationResponse,
  generateAISuggestions,
  uploadAttachment,
  getAttachment,
  getAttachmentsRemainingLimit,
  getGeneratedImagesRemainingLimit,
  getPromptsRemainingLimit,
  getGeneratedDocument,
  getGeneratedArtifacts,
  createPublicDocumentLink,
  getPublicDocument,
  updateGeneratedDocument,
  uploadImage,
  getGeneratedPodcast,
  updateGeneratedPodcast,
};

function generateLernsetWorksheet(payload: GenerateLernsetWorksheet) {
  notifyBugsnagNetwork('AI', 'generateLernsetWorksheet', { ...payload });
  return handleUnauthorized(() => api.ai.generateLernsetWorksheet(payload));
}

function streamConversationResponse(
  payload: AIConversationAction, abortSignal?: AbortSignal) {
  notifyBugsnagNetwork('AI', 'streamConversationResponse', { ...payload });
  return handleUnauthorized(() => api.ai.streamAiConversationResponse(payload, abortSignal));
}

function generateAISuggestions(payload: GenerateSuggestions) {
  notifyBugsnagNetwork('AI', 'generateAISuggestions', { ...payload });
  return handleUnauthorized(() => api.ai.generateSuggestions(payload));
}

function uploadAttachment(file: FormData, abortSignal?: AbortSignal) {
  notifyBugsnagNetwork('AI', 'uploadFile', { file });
  return handleUnauthorized(() => api.ai.uploadAttachment(file, abortSignal));
}

function getAttachment(attachmentHashName: string) {
  notifyBugsnagNetwork('AI', 'getAttachment', { attachmentHashName });
  return handleUnauthorized(() => api.ai.getAttachment(attachmentHashName));
}

function getAttachmentsRemainingLimit() {
  notifyBugsnagNetwork('AI', 'getAttachmentsRemainingLimit');
  return handleUnauthorized(() => api.ai.getAttachmentsRemainingLimit());
}

function getGeneratedImagesRemainingLimit() {
  notifyBugsnagNetwork('AI', 'getGeneratedImagesRemainingLimit');
  return handleUnauthorized(() => api.ai.getGeneratedImagesRemainingLimit());
}

function getPromptsRemainingLimit() {
  notifyBugsnagNetwork('AI', 'getPromptsRemainingLimit');
  return handleUnauthorized(() => api.ai.getPromptsRemainingLimit());
}

function getGeneratedDocument(documentId: string) {
  notifyBugsnagNetwork('AI', 'getGeneratedDocument', { documentId });
  return handleUnauthorized(() => api.ai.getGeneratedDocument(documentId));
}

function getGeneratedArtifacts(query?: GeneratedArtifactsQuery): Promise<AiGeneratedArtifacts> {
  notifyBugsnagNetwork('AI', 'getGeneratedArtifacts', { ...query });
  return handleUnauthorized(() => api.ai.getGeneratedArtifacts(query));
}

function createPublicDocumentLink(documentId: string) {
  notifyBugsnagNetwork('AI', 'createPublicDocumentLink', { documentId });
  return handleUnauthorized(() => api.ai.createPublicDocumentLink(documentId));
}

function getPublicDocument(accessToken: string) {
  notifyBugsnagNetwork('AI', 'getPublicDocument', { accessToken });
  return handleUnauthorized(() => api.ai.getPublicDocument(accessToken));
}

function updateGeneratedDocument(documentId: string, payload: Partial<Pick<AiGeneratedDocument, 'title' | 'content'>>) {
  notifyBugsnagNetwork('AI', 'updateGeneratedDocument', { documentId, ...payload });
  // Filter out undefined values to match API type requirements
  const filteredPayload: Partial<Pick<AiGeneratedDocument, 'title' | 'content'>> = {};
  if (payload.title !== undefined) {
    filteredPayload.title = payload.title;
  }
  if (payload.content !== undefined) {
    filteredPayload.content = payload.content;
  }
  return handleUnauthorized(() =>
    api.ai.updateGeneratedDocument(documentId, filteredPayload as Pick<AiGeneratedDocument, 'title' | 'content'>)
  );
}

function uploadImage(formData: FormData, abortSignal?: AbortSignal) {
  notifyBugsnagNetwork('AI', 'uploadImage', { formData });
  return handleUnauthorized(() => api.ai.uploadImage(formData, abortSignal));
}

function getGeneratedPodcast(podcastId: string, query?: GeneratedPodcastQuery) {
  notifyBugsnagNetwork('AI', 'getGeneratedPodcast', { podcastId, ...query });
  return handleUnauthorized(() => api.ai.getGeneratedPodcast(podcastId, query));
}

function updateGeneratedPodcast(podcastId: string, payload: UpdateGeneratedPodcast) {
  notifyBugsnagNetwork('AI', 'updateGeneratedPodcast', { podcastId, ...payload });
  return handleUnauthorized(() => api.ai.updateGeneratedPodcast(podcastId, payload));
}

export default service;
